import { z } from "zod";
import { requestQuestionFieldsSchema } from "./request-questions.js";
// The staff request queue and lifecycle contract of Milestone D, lane R2 (CHT-750; docs/architecture/milestone-d-contracts.md, sections
// 7.2 and 8). Routes MD-R1 to MD-R11, in `apps/api/src/routes/staff-request-routes.ts`. The admin calls them only through
// `@lafayette/api-client` (`staff-requests.ts`).
//
// Same rules as `conversation-handoff.ts`: strict top-level shapes, ISO 8601 text for times, closed codes repeated from the domain and
// parity-tested (`apps/api/src/routes/staff-request-routes.test.ts` compares each list with its domain tuple). STAFF ONLY: every shape
// here holds data a customer must never see (the work status, owner, assignee, reasons, internal notes). Nothing in this file is
// served on a customer route.
//
// Concurrency: every command carries the `expectedVersion` it read AND an `Idempotency-Key` header. A stale version, a wrong state or a
// request that was already taken is a 409 whose body carries `requestConflict`; the client refreshes and shows the canonical state.
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/u;
const uuid = z.string().regex(UUID);
const instant = z.iso.datetime({ offset: false });
const version = z.number().int().min(1).max(2_147_483_647);
/** A staff-internal reason. Never a customer view, a card, a notice, an audit row a customer reads, or analytics. */
const staffReason = z.string().trim().min(1).max(500);
const code = z.string().min(1).max(64);
// --- codes (parity-tested) --------------------------------------------------------------------------------------------------------
/** The queue filters (the closed list of the contract). No filter: every open request the caller sees. */
export const staffRequestQueueFilterValues = [
    "ASSIGNED_TO_ME",
    "UNASSIGNED",
    "SUBMITTED",
    "IN_PROGRESS",
    "AWAITING_CUSTOMER",
    "AWAITING_SUPPLIER",
    "PAYMENT_REQUIRED",
    "CONFIRMED",
    "COMPLETED",
    "UNAVAILABLE_OR_CANCELLED",
    "AGING",
];
export const staffRequestStateValues = [
    "DRAFT",
    "SUBMITTED",
    "ASSIGNED",
    "IN_PROGRESS",
    "AWAITING_CUSTOMER",
    "PAYMENT_REQUIRED",
    "CONFIRMED",
    "COMPLETED",
    "CANCELLED",
    "UNAVAILABLE",
    "DECLINED",
    "EXPIRED",
];
/** The customer-facing status code of each state, in state order (the minimal answer of a command carries it). */
export const staffRequestStatusCodeValues = [
    "REQUEST_DRAFT",
    "REQUEST_RECEIVED",
    "REQUEST_WITH_YOUR_CONCIERGE",
    "REQUEST_BEING_ARRANGED",
    "REQUEST_NEEDS_YOUR_INPUT",
    "REQUEST_AWAITING_YOUR_PAYMENT",
    "REQUEST_CONFIRMED",
    "REQUEST_COMPLETED",
    "REQUEST_CANCELLED",
    "REQUEST_UNAVAILABLE",
    "REQUEST_NOT_POSSIBLE",
    "REQUEST_EXPIRED",
];
export const staffRequestTypeValues = [
    "DINING",
    "VILLA",
    "EVENT",
    "EXPERIENCE",
    "SERVICE",
    "TRANSPORT",
    "OTHER",
];
export const staffRequestPriorityValues = ["STANDARD", "HIGH", "URGENT"];
/** Where the open work waits. Staff only. `NONE` outside IN_PROGRESS. */
export const staffRequestWorkStatusValues = ["NONE", "WITH_SUPPLIER", "INTERNAL_REVIEW"];
export const staffRequestAttentionFlagValues = [
    "UNASSIGNED",
    "AWAITING_CUSTOMER",
    "AGING",
];
export const staffRequestExceptionValues = ["COVERAGE_GAP"];
/**
 * CHT-753. The attention kinds that hold NOW for a request, read from its state and history, never from chat state (the domain's
 * `requestAttentionEventKinds`; parity-tested). `REQUEST_AGING` is a computed read of the optional threshold.
 */
export const staffRequestAttentionKindValues = [
    "REQUEST_SUBMITTED",
    "REQUEST_CUSTOMER_RESPONDED",
    "REQUEST_UNASSIGNED",
    "REQUEST_AGING",
];
/** The code of a 409 on a request command (`requestConflict.code`). */
export const requestConflictCodes = [
    "request.version_conflict",
    "request.transition_invalid",
    "request.terminal",
    "request.same_owner",
    "request.assignee_ineligible",
    "request.payment_request_ineligible",
    "request.payment_outcome_pending",
    "request.payment_already_settled",
    "request.not_expired_yet",
    "request.no_eligible_assignee",
];
export const staffRequestNoteKindValues = [
    "SUPPLIER_CONTACT",
    "SUPPLIER_QUOTE_CONTEXT",
    "STAFF_NOTE",
    "HANDOVER",
];
export const staffRequestCompletionEvidenceValues = [
    "SERVICE_DELIVERED",
    "CUSTOMER_ACKNOWLEDGED",
    "SUPPLIER_REPORTED_FULFILMENT",
];
// --- MD-R1: the queue ------------------------------------------------------------------------------------------------------------
export const staffRequestQueueQuerySchema = z.strictObject({
    filter: z.enum(staffRequestQueueFilterValues).optional(),
    /** Opaque: the keyset position of the last row of the previous page. Do not parse it. */
    cursor: z
        .string()
        .regex(/^[A-Za-z0-9_-]{1,200}$/u)
        .optional(),
    limit: z.coerce.number().int().min(1).max(100).default(25),
});
const personRef = z.strictObject({
    staffUserId: uuid,
    displayName: z.string().max(200).nullable(),
});
export const staffRequestQueueRowSchema = z.strictObject({
    id: uuid,
    title: z.string().min(1).max(140),
    requestType: z.enum(staffRequestTypeValues),
    state: z.enum(staffRequestStateValues),
    /** The `expectedVersion` of the next command on this row. */
    version,
    workStatus: z.enum(staffRequestWorkStatusValues),
    priority: z.enum(staffRequestPriorityValues),
    /** The owner while the request is open. */
    owner: personRef.nullable(),
    /** The designated assignee while ASSIGNED; `null` for a request in the shared supervisor queue. */
    assignee: personRef.nullable(),
    customer: z.strictObject({ id: uuid, displayName: z.string().min(1).max(120) }),
    /** What the request is about: the trip, the itinerary item, the catalog item, and the joined line. */
    context: z.strictObject({
        tripTitle: z.string().nullable(),
        itineraryItemTitle: z.string().nullable(),
        catalogItemName: z.string().nullable(),
        line: z.string().nullable(),
    }),
    submittedAt: instant.nullable(),
    awaitingCustomerSince: instant.nullable(),
    stateChangedAt: instant,
    /** Seconds since submission: data the screen shows, not an SLA. */
    ageSeconds: z.number().int().min(0).nullable(),
    flags: z.array(z.enum(staffRequestAttentionFlagValues)).max(3),
    /** `COVERAGE_GAP`: submitted and routing left it with no owner. A supervisor assigns it (`reassign`). */
    exception: z.enum(staffRequestExceptionValues).nullable(),
    /** CHT-753. The newest action of the customer on the request (submit, answer, cancel). Data for the screen; `null`: none. */
    lastCustomerActivityAt: instant.nullable(),
    /** CHT-753. The newest answer of the customer. `null`: the customer never answered. */
    customerRespondedAt: instant.nullable(),
    /** CHT-753. What needs a person now: a new request, an answer no staff action followed, no named assignee, aging. */
    attention: z.array(z.enum(staffRequestAttentionKindValues)).max(4),
});
export const staffRequestQueuePageSchema = z.strictObject({
    filter: z.enum(staffRequestQueueFilterValues).nullable(),
    rows: z.array(staffRequestQueueRowSchema).max(100),
    nextCursor: z
        .string()
        .regex(/^[A-Za-z0-9_-]{1,200}$/u)
        .nullable(),
});
// --- MD-R2: the detail -------------------------------------------------------------------------------------------------------------
// Staff read everything the use case returns once it has authorized them, so the row, the notes and the history are not re-listed key
// by key (the row carries every column of the request, work status included). The new parts are strict.
const staffRecord = z.record(z.string(), z.unknown());
export const staffRequestDetailSchema = z.strictObject({
    request: staffRecord,
    internalNotes: z.array(staffRecord),
    /** The full append-only history, oldest first: the event, the states, the actor, the staff reason and the evidence. */
    transitions: z.array(staffRecord),
    customer: z.strictObject({
        id: uuid,
        displayName: z.string().min(1).max(120),
        /** What the customer set in their own profile (the same facts the handoff summary shows staff). */
        preferences: z.strictObject({
            preferredName: z.string().nullable(),
            locale: z.string().nullable(),
            homeMarket: z.string().nullable(),
            contactChannel: z.string().nullable(),
        }),
    }),
    context: z.strictObject({
        conversationId: uuid.nullable(),
        tripId: uuid.nullable(),
        tripTitle: z.string().nullable(),
        itineraryItemId: uuid.nullable(),
        itineraryItemTitle: z.string().nullable(),
        catalogItemId: uuid.nullable(),
        catalogItemName: z.string().nullable(),
        line: z.string().nullable(),
    }),
    ownership: z.strictObject({
        assignmentSource: z
            .enum(["TEMPORARY", "PRIMARY", "BACKUP", "SUPERVISOR_QUEUE", "DIRECT"])
            .nullable(),
        assignee: personRef.nullable(),
        owner: personRef.nullable(),
        claimedAt: instant.nullable(),
        history: z.array(z.strictObject({
            requestVersion: version,
            event: z.enum(["ASSIGNED", "CLAIMED", "OWNER_REASSIGNED"]),
            occurredAt: instant,
            byActorKind: z.enum(["staff", "system", "customer"]),
            byStaffUserId: uuid.nullable(),
            toStaffUserId: uuid.nullable(),
            /** How the holder was chosen: a routing kind, or `DIRECT`. */
            code: code.nullable(),
        })),
    }),
    /** Display names of the staff the request, its notes and its history name, by staff user id. */
    actors: z.record(z.string(), z.string()),
    attention: z.strictObject({
        ageSeconds: z.number().int().min(0).nullable(),
        awaitingCustomerSince: instant.nullable(),
        flags: z.array(z.enum(staffRequestAttentionFlagValues)).max(3),
        exception: z.enum(staffRequestExceptionValues).nullable(),
    }),
});
// --- MD-R3 to MD-R11: the commands ------------------------------------------------------------------------------------------------
/** The body of every command that needs only the version and a reason: claim, complete (plus evidence), cancel, decline, unavailable. */
export const staffRequestVersionReasonBodySchema = z.strictObject({
    expectedVersion: version,
    reason: staffReason,
});
/** MD-R3 claim. The signed-in staff user becomes the owner; a body never names one. */
export const staffRequestClaimBodySchema = staffRequestVersionReasonBodySchema;
/**
 * MD-R4 reassign. One route: a SUBMITTED request that routing could not place is assigned (a coverage gap), any other open request
 * changes its designated assignee (while ASSIGNED) or its owner. Supervisor only.
 */
export const staffRequestReassignBodySchema = z.strictObject({
    expectedVersion: version,
    reason: staffReason,
    assigneeStaffUserId: uuid,
});
/**
 * MD-R5 ask the customer. `question` is the prompt. With `fields` (1 to 8 typed fields, CHT-751) the customer answers a form; without, the
 * question is plain text and the answer is one `reply`.
 */
export const staffRequestAskCustomerBodySchema = z.strictObject({
    expectedVersion: version,
    reason: staffReason,
    question: z.string().trim().min(1).max(1000),
    fields: requestQuestionFieldsSchema.optional(),
});
/** MD-R6 work status: where the open work waits. The state does not change. */
export const staffRequestWorkStatusBodySchema = z.strictObject({
    expectedVersion: version,
    reason: staffReason,
    workStatus: z.enum(staffRequestWorkStatusValues),
});
/**
 * MD-R7 unavailable, MD-R10 cancel and decline: the optional `customerStatusSummary` is the line the customer sees as the status.
 * A staff reason is not shown to a customer.
 */
export const staffRequestClosureBodySchema = z.strictObject({
    expectedVersion: version,
    reason: staffReason,
    customerStatusSummary: z.string().trim().min(1).max(500).nullish(),
});
/**
 * MD-R8 confirm (CHT-752). The body names the staff-recorded evidence row and nothing else of the evidence: a kind and a reference typed
 * here are an unknown key (`400`). The row (`POST .../evidence`, a fresh `CONFIRMATION_REFERENCE`) supplies them, and the database
 * guard LF057 checks it.
 */
export const staffRequestConfirmBodySchema = z.strictObject({
    expectedVersion: version,
    reason: staffReason,
    evidenceId: uuid,
});
/** MD-R9 complete: operational closure evidence. */
export const staffRequestCompleteBodySchema = z.strictObject({
    expectedVersion: version,
    reason: staffReason,
    evidenceKind: z.enum(staffRequestCompletionEvidenceValues),
});
/** MD-R11 priority. */
export const staffRequestPriorityBodySchema = z.strictObject({
    expectedVersion: version,
    reason: staffReason,
    priority: z.enum(staffRequestPriorityValues),
});
/** MD-R11 status summary: the line the customer sees as the status of their request (at most 500 characters). */
export const staffRequestStatusSummaryBodySchema = z.strictObject({
    expectedVersion: version,
    reason: staffReason,
    summary: z.string().trim().min(1).max(500),
});
/** MD-R11 internal note: append-only, staff-internal, no version (a note is not a change of the request). */
export const staffRequestNoteBodySchema = z.strictObject({
    kind: z.enum(staffRequestNoteKindValues),
    body: z.string().trim().min(1).max(4000),
});
/**
 * The success body of a command (CHT-750 review, F2): the minimal outcome the command committed, plus the canonical detail read afterwards
 * through the normal authorised path. `detail` is `null` when the caller may not read the request any more (a designated assignee who
 * cancelled it, an ended relationship, a reassignment in between): then the answer carries no notes, history or other staff.
 */
export const staffRequestCommandResultSchema = z.strictObject({
    requestId: uuid,
    state: z.enum(staffRequestStateValues),
    version,
    statusCode: z.enum(staffRequestStatusCodeValues),
    detail: staffRequestDetailSchema.nullable(),
});
/** The success body of an internal note: ids only (the note text is never echoed). */
export const staffRequestNoteResultSchema = z.strictObject({ requestId: uuid, noteId: uuid });
/**
 * The extra key `requestConflict` of a 409 on a request command. The client refreshes: `currentVersion` and `currentState` are
 * what the caller may read, or `null` when they may not read the request.
 */
export const requestConflictSchema = z.strictObject({
    code: z.enum(requestConflictCodes),
    currentVersion: version.nullable(),
    currentState: z.enum(staffRequestStateValues).nullable(),
});
/** The paths of the staff request routes. Ids are encoded by the client, so an id can never add a path segment. */
export const staffRequestActionValues = [
    "claim",
    "reassign",
    "ask-customer",
    "work-status",
    "unavailable",
    "decline",
    "confirm",
    "complete",
    "cancel",
    "priority",
    "status-summary",
    "notes",
];
// --- the staff directory and "who am I" (CHT-754 follow-up) ---------------------------------------------------------------------
/** The work roles the directory shows. No other role decides who can take a request. */
export const staffAssigneeRoleValues = ["concierge", "supervisor"];
/** One person a request can be given to. Closed list: the id, the display name and the work roles. No email, phone or auth id. */
export const staffRequestAssigneeSchema = z.strictObject({
    staffUserId: uuid,
    displayName: z.string().min(1).max(200),
    roles: z.array(z.enum(staffAssigneeRoleValues)).min(1).max(2),
});
/** `GET /staff/request-assignees` (`request.work`). The list is a convenience: the reassign command decides again. */
export const staffRequestAssigneesSchema = z.strictObject({
    assignees: z.array(staffRequestAssigneeSchema).max(200),
});
/** The request capabilities the admin screens show or hide actions for (parity-tested with `staffSelfCapabilities`). */
export const staffSelfCapabilityValues = [
    "request.work",
    "request.reassign_owner",
    "request.internal_notes",
    "request.read_internal",
];
/** `GET /staff/me`: the caller's own staff id, display name, roles and request capabilities. About the caller only. */
export const staffSelfSchema = z.strictObject({
    staffUserId: uuid,
    displayName: z.string().min(1).max(200),
    roles: z.array(z.enum(["concierge", "supervisor", "catalog_editor", "administrator"])).max(4),
    capabilities: z.array(z.enum(staffSelfCapabilityValues)).max(4),
});
